import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { apartmentsApi } from '../../api/apartmentsApi';
import { paymentApi, ReceivableFee } from '../../api/paymentApi';
import { downloadExcel, sanitizeExportFilename } from '../../utils/excelExport';
import { UNASSIGNED_APARTMENT_GROUP_ID } from '../../constants/apartmentGroups';
import ApartmentServiceFeePaymentModal from '../../components/modals/ApartmentServiceFeePaymentModal';
import ReceivableDebtCleanupModal from './ReceivableDebtCleanupModal';
import toast from 'react-hot-toast';

interface Building {
  id: string;
  name: string;
}

interface ApartmentGroupOption {
  id: string;
  name: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(amount));

interface DebtTotals {
  receivableAmount: number;
  paidAmount: number;
  remainingDebt: number;
  surplusBalance: number;
}

const computeTotals = (rows: ReceivableFee[]): DebtTotals =>
  rows.reduce(
    (acc, row) => ({
      receivableAmount: acc.receivableAmount + (row.receivableAmount ?? row.incurredAmount),
      paidAmount: acc.paidAmount + row.paidAmount,
      remainingDebt: acc.remainingDebt + row.remainingDebt,
      surplusBalance: acc.surplusBalance + (row.surplusBalance ?? 0),
    }),
    {
      receivableAmount: 0,
      paidAmount: 0,
      remainingDebt: 0,
      surplusBalance: 0,
    },
  );

export const ReceivableDebtTab: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [groups, setGroups] = useState<ApartmentGroupOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [residentSearch, setResidentSearch] = useState('');
  const [viewOverdue, setViewOverdue] = useState(false);
  const [fees, setFees] = useState<ReceivableFee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [tableSearch, setTableSearch] = useState('');
  const [paymentModal, setPaymentModal] = useState<{
    apartmentId: string;
    apartmentCode: string;
  } | null>(null);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    }).catch(console.error);
  }, [user]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setGroups([]);
      setSelectedGroupIds([]);
      return;
    }
    apartmentsApi.getApartmentGroups(selectedBuildingId)
      .then((data) => setGroups(data || []))
      .catch(console.error);
  }, [selectedBuildingId]);

  const loadReport = useCallback(async () => {
    if (!selectedBuildingId) {
      setFees([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await paymentApi.getReceivableReport({
        buildingId: selectedBuildingId,
        overdue: viewOverdue,
        apartmentGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        apartmentSearch,
        residentSearch,
      });
      setFees(data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching receivable fees:', error);
      toast.error('Lỗi khi tải báo cáo phí phải thu');
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedBuildingId,
    viewOverdue,
    selectedGroupIds,
    apartmentSearch,
    residentSearch,
  ]);

  useEffect(() => {
    if (!selectedBuildingId) return;
    const timer = setTimeout(() => {
      loadReport();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadReport, selectedBuildingId]);

  const filteredFees = useMemo(
    () =>
      fees.filter((fee) => {
        if (!tableSearch.trim()) return true;
        const q = tableSearch.toLowerCase();
        return (
          fee.apartmentCode.toLowerCase().includes(q) ||
          (fee.apartmentName || '').toLowerCase().includes(q) ||
          fee.ownerName.toLowerCase().includes(q)
        );
      }),
    [fees, tableSearch],
  );

  const totals = useMemo(() => computeTotals(filteredFees), [filteredFees]);

  const totalPages = Math.ceil(filteredFees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFees = filteredFees.slice(startIndex, endIndex);

  const selectedBuildingName =
    buildings.find((b) => b.id === selectedBuildingId)?.name || 'bao-cao';

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const handleExportData = () => {
    if (filteredFees.length === 0) {
      toast.error('Không có dữ liệu để xuất');
      return;
    }

    const headers = [
      'STT',
      'Mã căn hộ',
      'Chủ hộ',
      'Nợ phải thu',
      'Đã thanh toán',
      'Còn nợ',
      'Thu dư',
    ];
    const rows = filteredFees.map((fee, index) => [
      index + 1,
      fee.apartmentCode,
      fee.ownerName,
      fee.receivableAmount ?? fee.incurredAmount,
      fee.paidAmount,
      fee.remainingDebt,
      fee.surplusBalance,
    ]);
    rows.push([
      '',
      'Tổng cộng',
      `${filteredFees.length} căn hộ`,
      totals.receivableAmount,
      totals.paidAmount,
      totals.remainingDebt,
      totals.surplusBalance,
    ]);

    const filename = `${sanitizeExportFilename(`cong-no_${selectedBuildingName}`)}.xlsx`;
    downloadExcel('Công nợ', headers, rows, filename);
    toast.success('Đã xuất dữ liệu');
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Chung cư</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">Chọn chung cư</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>{building.name}</option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Căn hộ</label>
            <input
              type="text"
              value={apartmentSearch}
              onChange={(e) => setApartmentSearch(e.target.value)}
              placeholder="Mã căn hộ, số căn hộ..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              disabled={!selectedBuildingId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cư dân</label>
            <input
              type="text"
              value={residentSearch}
              onChange={(e) => setResidentSearch(e.target.value)}
              placeholder="Tên, mã cư dân..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              disabled={!selectedBuildingId}
            />
          </div>
        </div>

        {selectedBuildingId && groups.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nhóm căn hộ</label>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedGroupIds.includes(UNASSIGNED_APARTMENT_GROUP_ID)}
                  onChange={() => toggleGroup(UNASSIGNED_APARTMENT_GROUP_ID)}
                  className="rounded border-gray-300 text-cyan-600"
                />
                <span>Chưa gán nhóm</span>
              </label>
              {groups.map((group) => (
                <label key={group.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    className="rounded border-gray-300 text-cyan-600"
                  />
                  <span>{group.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={viewOverdue}
            onChange={(e) => setViewOverdue(e.target.checked)}
            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            disabled={!selectedBuildingId}
          />
          <span className="ml-2 text-sm text-gray-700">
            Chỉ xem phí đã quá hạn thanh toán
          </span>
        </label>
      </div>

      {selectedBuildingId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Công nợ
              {filteredFees.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({filteredFees.length} căn hộ)
                </span>
              )}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              >
                <option value={25}>25 / trang</option>
                <option value={50}>50 / trang</option>
                <option value={100}>100 / trang</option>
              </select>
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => {
                  setTableSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Lọc nhanh..."
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm min-w-[160px]"
              />
              <button
                type="button"
                onClick={handleExportData}
                disabled={filteredFees.length === 0}
                className="px-3 py-1.5 border border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 text-sm font-medium disabled:opacity-50"
              >
                Xuất dữ liệu
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setCleanupModalOpen(true)}
                  className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium"
                >
                  Xóa nhập công nợ cũ
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-cyan-50">
                    <th className="border border-gray-300 px-3 py-3 text-left font-semibold">#</th>
                    <th className="border border-gray-300 px-3 py-3 text-left font-semibold">Căn hộ</th>
                    <th className="border border-gray-300 px-3 py-3 text-left font-semibold">Chủ hộ</th>
                    <th className="border border-gray-300 px-3 py-3 text-right font-semibold">Nợ phải thu</th>
                    <th className="border border-gray-300 px-3 py-3 text-right font-semibold">Đã thanh toán</th>
                    <th className="border border-gray-300 px-3 py-3 text-right font-semibold">Còn nợ</th>
                    <th className="border border-gray-300 px-3 py-3 text-right font-semibold">Thu dư</th>
                    <th className="border border-gray-300 px-3 py-3 text-center font-semibold w-28">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="border border-gray-300 px-4 py-10 text-center text-gray-500">
                        Không có công nợ phí dịch vụ
                      </td>
                    </tr>
                  ) : (
                    <>
                      <tr className="bg-yellow-50 font-bold">
                        <td className="border border-gray-300 px-3 py-2" colSpan={3}>
                          Tổng cộng ({filteredFees.length})
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right">
                          {formatCurrency(totals.receivableAmount)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right">
                          {formatCurrency(totals.paidAmount)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-red-600">
                          {formatCurrency(totals.remainingDebt)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-emerald-600">
                          {formatCurrency(totals.surplusBalance)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2" />
                      </tr>
                      {currentFees.map((fee, index) => (
                        <tr
                          key={fee.apartmentId}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="border border-gray-300 px-3 py-2">{startIndex + index + 1}</td>
                          <td className="border border-gray-300 px-3 py-2">
                            <Link
                              to={`/apartments/${fee.apartmentId}/details?tab=serviceFees`}
                              className="text-cyan-600 font-medium hover:text-cyan-800 hover:underline"
                            >
                              {fee.apartmentCode}
                            </Link>
                            {fee.apartmentName && (
                              <span className="text-gray-500 text-xs block">{fee.apartmentName}</span>
                            )}
                          </td>
                          <td className="border border-gray-300 px-3 py-2">{fee.ownerName}</td>
                          <td className="border border-gray-300 px-3 py-2 text-right">
                            {formatCurrency(fee.receivableAmount ?? fee.incurredAmount)}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-right">
                            {formatCurrency(fee.paidAmount)}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-right text-red-600 font-medium">
                            {formatCurrency(fee.remainingDebt)}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-right text-emerald-600">
                            {formatCurrency(fee.surplusBalance)}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setPaymentModal({
                                  apartmentId: fee.apartmentId,
                                  apartmentCode: fee.apartmentCode,
                                })
                              }
                              className="text-cyan-600 hover:text-cyan-800 font-medium text-sm hover:underline"
                            >
                              Thanh toán
                            </button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-700">
                Đang xem {startIndex + 1} đến {Math.min(endIndex, filteredFees.length)} trong tổng số {filteredFees.length} bản ghi.
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                >
                  ←
                </button>
                <span className="text-sm text-gray-600">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {paymentModal && (
        <ApartmentServiceFeePaymentModal
          isOpen={true}
          apartmentId={paymentModal.apartmentId}
          apartmentCode={paymentModal.apartmentCode}
          onClose={() => setPaymentModal(null)}
          onSuccess={() => loadReport()}
        />
      )}

      {isSuperAdmin && selectedBuildingId && (
        <ReceivableDebtCleanupModal
          isOpen={cleanupModalOpen}
          buildingId={selectedBuildingId}
          buildingName={selectedBuildingName}
          onClose={() => setCleanupModalOpen(false)}
          onCleared={() => loadReport()}
        />
      )}

    </div>
  );
};

export default ReceivableDebtTab;
